/**
 * Custom Hook for Game AI
 * Provides Easy, Medium, Hard, and Lichess API difficulty levels
 * Reusable across all games with computer opponents
 */

import { useState, useCallback } from 'react';

export const useGameAI = () => {
  const [aiThinking, setAiThinking] = useState(false);

  /**
   * Get best move from Lichess Cloud Eval API
   * Requires position in FEN notation
   */
  const getBestMoveFromLichessAPI = useCallback(async (fen) => {
    try {
      setAiThinking(true);
      console.log('Requesting Lichess evaluation for FEN:', fen);

      const response = await fetch(
        `https://lichess.org/api/cloud-eval?fen=${encodeURIComponent(fen)}`
      );

      if (!response.ok) {
        console.error('Lichess API error:', response.status);
        setAiThinking(false);
        return null;
      }

      const data = await response.json();
      console.log('Lichess API response:', data);

      if (data.pvs && data.pvs.length > 0 && data.pvs[0].moves) {
        const moveStr = data.pvs[0].moves[0];
        console.log('Best move from Lichess:', moveStr);
        setAiThinking(false);
        return moveStr;
      }

      setAiThinking(false);
      return null;
    } catch (err) {
      console.error('Lichess API error:', err);
      setAiThinking(false);
      return null;
    }
  }, []);

  /**
   * Easy difficulty: Random move selection
   * Instant, suitable for beginners
   */
  const getEasyMove = useCallback((possibleMoves) => {
    if (!possibleMoves || possibleMoves.length === 0) return null;
    const randomIndex = Math.floor(Math.random() * possibleMoves.length);
    return possibleMoves[randomIndex];
  }, []);

  /**
   * Medium difficulty: Heuristic-based scoring
   * Prioritizes captures and checks
   */
  const getMediumMove = useCallback(
    (possibleMoves, scoreFunc) => {
      if (!possibleMoves || possibleMoves.length === 0) return null;

      const scoredMoves = possibleMoves.map((move) => ({
        ...move,
        score: scoreFunc(move),
      }));

      const maxScore = Math.max(...scoredMoves.map((m) => m.score));
      const bestMoves = scoredMoves.filter(
        (m) => m.score >= maxScore - 3
      );

      const randomIndex = Math.floor(Math.random() * bestMoves.length);
      return bestMoves[randomIndex];
    },
    []
  );

  /**
   * Hard difficulty: Minimax-like piece valuation
   * Evaluates board state based on piece values
   */
  const getHardMove = useCallback(
    (possibleMoves, scoreFunc) => {
      if (!possibleMoves || possibleMoves.length === 0) return null;

      const scoredMoves = possibleMoves.map((move) => ({
        ...move,
        score: scoreFunc(move),
      }));

      const maxScore = Math.max(...scoredMoves.map((m) => m.score));
      const bestMoves = scoredMoves.filter((m) => m.score === maxScore);

      const randomIndex = Math.floor(Math.random() * bestMoves.length);
      return bestMoves[randomIndex];
    },
    []
  );

  /**
   * Main AI function - handles all difficulty levels
   * @param {string} difficulty - 'easy', 'medium', 'hard', or 'ai'
   * @param {array} possibleMoves - Array of available moves
   * @param {function} scoreFunc - Custom scoring function for medium/hard
   * @param {string} fen - FEN string (required for 'ai' difficulty)
   * @param {function} convertMove - Function to convert move format (for 'ai')
   * @returns {object} - The selected move
   */
  const getAIMove = useCallback(
    async (
      difficulty,
      possibleMoves,
      scoreFunc = null,
      fen = null,
      convertMove = null
    ) => {
      try {
        switch (difficulty) {
          case 'easy':
            return getEasyMove(possibleMoves);

          case 'medium':
            if (!scoreFunc) {
              console.warn('scoreFunc not provided for medium difficulty');
              return getEasyMove(possibleMoves);
            }
            return getMediumMove(possibleMoves, scoreFunc);

          case 'hard':
            if (!scoreFunc) {
              console.warn('scoreFunc not provided for hard difficulty');
              return getEasyMove(possibleMoves);
            }
            return getHardMove(possibleMoves, scoreFunc);

          case 'ai':
            if (!fen || !convertMove) {
              console.error('FEN and convertMove function required for AI difficulty');
              return getHardMove(possibleMoves, scoreFunc) || getEasyMove(possibleMoves);
            }
            const moveStr = await getBestMoveFromLichessAPI(fen);
            if (moveStr) {
              return convertMove(moveStr);
            }
            // Fallback to Hard if API fails
            return getHardMove(possibleMoves, scoreFunc) || getEasyMove(possibleMoves);

          default:
            console.warn(`Unknown difficulty: ${difficulty}`);
            return getEasyMove(possibleMoves);
        }
      } catch (err) {
        console.error('AI error:', err);
        // Ultimate fallback: random move
        return getEasyMove(possibleMoves);
      }
    },
    [getEasyMove, getMediumMove, getHardMove, getBestMoveFromLichessAPI]
  );

  return {
    getAIMove,
    getBestMoveFromLichessAPI,
    getEasyMove,
    getMediumMove,
    getHardMove,
    aiThinking,
    setAiThinking,
  };
};

export default useGameAI;
